import { Link, useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import type {
  MarketingEmailTemplate,
  MarketingEmailTemplateContentType,
} from "@/react/types/MarketingEmailTemplates";
import { Button } from "@circle-react-uikit/Button";
import { Typography } from "@circle-react-uikit/Typography";
import { templateLocaleNamespace } from "../UpsertTemplateModal/UpdateTemplateContent";
import { CardOptions } from "./CardOptions";
import { ScaledTemplatePreview } from "./TemplateContentElements/ScaledTemplatePreview";
import { useDeleteTemplate } from "./hooks/useDeleteTemplate";
import { useRenameTemplate } from "./hooks/useRenameTemplate";

export function TemplateCard({
  template,
  onApply,
}: {
  template: MarketingEmailTemplate;
  onApply: (
    templateId: number,
    contentType: MarketingEmailTemplateContentType,
  ) => void;
}) {
  const history = useHistory();
  const { deleteModalElement, openDeleteModal } = useDeleteTemplate({
    template,
  });
  const { renameTemplateElement, openRenameTemplateModal } = useRenameTemplate({
    template,
  });

  return (
    <>
      <Link
        to={{
          pathname: `/templates/${template.id}`,
          state: {
            template,
          },
        }}
        className="group flex flex-col gap-3"
      >
        <div className="relative h-[360px] w-[260px] overflow-hidden rounded-[0.5rem] after:absolute after:inset-0 after:z-10 after:rounded-[inherit] after:transition-colors after:duration-300 group-hover:after:bg-black/80">
          <ScaledTemplatePreview template={template} />
          <button
            type="button"
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
            }}
            className="absolute inset-0 cursor-default"
            aria-hidden="true"
            tabIndex={-1}
          >
            <div className="absolute right-3 top-3 z-30 hidden group-hover:block">
              <CardOptions
                openDeleteModal={openDeleteModal}
                openRenameTemplateModal={openRenameTemplateModal}
              />
            </div>
            <div className="pointer-events-none absolute inset-0 z-20 flex size-full flex-col items-center justify-center space-y-2 bg-black/70 opacity-0 transition-opacity duration-300 group-hover:pointer-events-auto group-hover:opacity-100">
              <Button
                variant="secondary"
                type="button"
                onClick={() => onApply(template.id, template.content_type)}
                className="!w-[9.25rem]"
              >
                {t([templateLocaleNamespace, "apply"])}
              </Button>
              <Button
                variant="secondary-light"
                type="button"
                className="!text-badge hover:!bg-overlay dark:!text-darkest !w-[9.25rem] !bg-transparent"
                onClick={() => {
                  history.push({
                    pathname: `/templates/${template.id}`,
                    state: {
                      template,
                    },
                  });
                }}
              >
                {t([templateLocaleNamespace, "preview"])}
              </Button>
            </div>
          </button>
        </div>
        <Typography.LabelSm weight="semibold" truncate>
          {template.name}
        </Typography.LabelSm>
      </Link>
      {deleteModalElement}
      {renameTemplateElement}
    </>
  );
}
